import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface Consent {
    source: string; patient: string | null; granted_to: string | null; consent_type: string;
    status: string; purpose: string; start_date: string | null; end_date: string | null;
}

const STATUS_COLORS: Record<string, string> = {
    granted: 'success', active: 'success', approved: 'success',
    pending: 'warning', not_given: 'secondary', revoked: 'danger', expired: 'secondary',
};

const ConsentRegistry: React.FC = () => {
    const { api } = useInternalApi();
    const [consents, setConsents] = useState<Consent[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [sourceFilter, setSourceFilter] = useState('');

    useEffect(() => { document.title = 'Consent Registry - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('compliance-api/consent-registry/').then(res => {
            if (res.data?.status === 'success') {
                setConsents(res.data.data.consents || []);
                setSummary(res.data.data.summary);
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = sourceFilter ? consents.filter(c => c.source === sourceFilter) : consents;

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFileSignature} className="me-2 text-primary" />Consent Registry</h2>
            <p className="text-muted mb-4">Who consented to what, when — unified across every consent source in the platform</p>

            <div className="alert alert-secondary small">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                This reads the consent records the platform already keeps (medical-record sharing consents and service-request consents) rather than introducing a separate parallel consent store.
            </div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    {summary && (
                        <div className="row mb-4">
                            <div className="col-md-3 mb-3">
                                <div className="card border-0 shadow-sm"><div className="card-body">
                                    <div className="text-muted small mb-1">Record-sharing consents</div><h4>{summary.medical_record_consents}</h4>
                                </div></div>
                            </div>
                            <div className="col-md-3 mb-3">
                                <div className="card border-0 shadow-sm"><div className="card-body">
                                    <div className="text-muted small mb-1">Service-request consents</div><h4>{summary.service_request_consents}</h4>
                                </div></div>
                            </div>
                            <div className="col-md-3 mb-3">
                                <div className="card border-0 shadow-sm"><div className="card-body">
                                    <div className="text-muted small mb-1">Service requests w/ consent</div>
                                    <h4>{summary.service_requests_with_consent} <small className="text-muted">/ {summary.service_requests_total}</small></h4>
                                </div></div>
                            </div>
                        </div>
                    )}

                    <select className="form-select mb-3" style={{ maxWidth: 260 }} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                        <option value="">All sources</option>
                        <option value="medical_record">Medical record sharing</option>
                        <option value="service_request">Service requests</option>
                    </select>

                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            {filtered.length === 0 ? (
                                <div className="text-center py-5 text-muted">No consent records found.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Patient</th><th>Consent Type</th><th>Granted To</th><th>Status</th><th>Purpose</th><th>From</th><th>Until</th></tr></thead>
                                    <tbody>
                                        {filtered.map((c, i) => (
                                            <tr key={i}>
                                                <td>{c.patient || '—'}</td>
                                                <td>{c.consent_type}<div className="small text-muted">{c.source.replace('_', ' ')}</div></td>
                                                <td>{c.granted_to || '—'}</td>
                                                <td><span className={`badge bg-${STATUS_COLORS[c.status] || 'secondary'}`}>{c.status.replace('_', ' ')}</span></td>
                                                <td className="small">{c.purpose || '—'}</td>
                                                <td className="small">{c.start_date ? new Date(c.start_date).toLocaleDateString() : '—'}</td>
                                                <td className="small">{c.end_date ? new Date(c.end_date).toLocaleDateString() : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default ConsentRegistry;
